import { type MenuPage, type MenuTab } from "./shared";
import { touchCloseHint } from "../../input/touch";

/*
 * The menu: four pages of one screen where three overlays used to be
 * (chunk U-3, `ui-lab/surfaces/menu-tabs.html`, plan §5 "U-3").
 *
 * Until this chunk the client opened three unrelated boxes — the journal on J
 * in the middle of the screen, the map on M in the same place, what is in the
 * hands on I down the left — each with its own close key, and nothing on any of
 * them saying the other two existed. They are pages of one screen now, with a
 * strip across the top, and the reorganisation buys three things the three
 * boxes could not:
 *
 * - **one way out.** Every page closes the way every other one does, which is
 *   what three close keys could never give; the foot says it once.
 * - **a page for the player's own figure.** The journal was carrying the
 *   introduction line in its lead only because there was nowhere else to put it
 *   (Phase 5 decision 6c). There is somewhere else now.
 * - **the keys keep working.** `J`, `M` and `I` land on their own page and
 *   close it again, so no muscle memory is spent (`input/controls.ts`).
 *
 * # What this file owns, and what it does not
 *
 * It owns the screen: the veil, the frame, the strip, the four page shells and
 * the foot. It owns **nothing** that is written on a page — the journal fills
 * two of them (`hud/panels/journal.ts`: the ledger and the you page, which are
 * two readings of one view), `hud/panels/inventory.ts` one and `map/panel.ts`
 * one. What passes between them is {@link MenuTab}, which is four verbs wide
 * and is declared in `shared.ts` so the map does not have to import the menu to
 * be a page of it.
 *
 * # Blur: allowed here, measured, and dropped
 *
 * `backdrop-filter` composites over a live three.js frame and is the kit's one
 * real cost, so plan decision D6 allows it exactly on surfaces that **free the
 * pointer** — and this screen is the first one in the client that genuinely
 * does, since it joins `hud/hud.ts`'s reading list and the mouse comes back the
 * frame it opens. So it was the one surface entitled to the kit's glass, D6
 * says in the same breath that it is *measured*, and it was: a full-frame blur
 * nearly doubles the cost of a frame on all three of the frames this client is
 * driven at. It is cancelled and the plate deepens to pay for it. The three
 * numbers are in `ui/menu.css` beside the declaration.
 */

/** The four pages, in the order the strip draws them. */
const PAGES: readonly { readonly page: MenuPage; readonly label: string }[] = [
  // The player's own figure first, because it is what the screen is *about*
  // before it is about anything they are carrying.
  { page: "you", label: "you" },
  { page: "journal", label: "journal" },
  { page: "map", label: "map" },
  { page: "inventory", label: "inventory" },
];

/**
 * The `hud__panel--*` name each page's body carries.
 *
 * **Three of the four are names that already existed**, and keeping them is
 * plan decision D2 rather than an economy: they are asserted by string in the
 * self-checks and in all three smoke scripts, nothing type-checks them, and a
 * page whose `hidden` still means *is this on screen* is a page every driver
 * already knows how to ask about. The fourth is minted.
 *
 * `hud__panel--deed` is **never** reused (`docs/decisions/witness.md` §5): the
 * deleted card is asserted absent in five places, and a page wearing its name
 * would fail all five.
 */
const PAGE_NAMES: Readonly<Record<MenuPage, string>> = {
  you: "hud__panel--you",
  journal: "hud__panel--journal",
  map: "hud__panel--map",
  inventory: "hud__panel--inventory",
};

/** One page: its shell, its tab and the sentence it adds to the foot. */
interface Page {
  readonly tab: HTMLButtonElement;
  readonly body: HTMLElement;
  note: string;
}

/**
 * The system screen: *you*, *journal*, *map*, *inventory*, opened with `Tab`.
 *
 * **Constructed where the journal used to be** (`hud/hud.ts`), because in this
 * HUD the constructor order *is* the z-order — there is no `z-index` anywhere
 * in the client (invariant 1) — and the three surfaces it absorbs were built at
 * that point in the stack. Everything that covered them still covers it.
 */
export class MenuScreen {
  private readonly element: HTMLElement;
  private readonly strip: HTMLElement;
  private readonly closeLine: HTMLElement;
  private readonly noteLine: HTMLElement;
  private readonly pages = new Map<MenuPage, Page>();

  /**
   * Which page is showing, or `null` when the screen is shut.
   *
   * One field for two facts, deliberately: *the screen is open* and *this page
   * is on it* are the same question asked twice, and the three absorbed
   * surfaces answer `open` with the second half of it.
   */
  private showing: MenuPage | null = null;

  /**
   * The page a bare `Tab` comes back to.
   *
   * The screen remembers where the player left it and the first opening of a
   * session is *you*, which is the page that says who they are. Kept when the
   * screen closes, because coming back to what you were reading is what a
   * player means by pressing the key twice.
   */
  private last: MenuPage = "you";

  /**
   * Who fills a page that is being *switched to*, or `null` before anybody has
   * said.
   *
   * The strip is the one door into a page that no key opens, so it is the one
   * door that has to ask for the page to be filled. `main.ts` registers the
   * answer ({@link MenuScreen.fills}) because the witnessing layer is what
   * holds the world, and that file already owns the three toggles the keys
   * drain into.
   */
  private filler: ((page: MenuPage) => void) | null = null;

  /**
   * `touch` is whether this session is played with fingers, and it reaches one
   * thing: whether the foot names a key or the button that closes the screen
   * (`input/touch.ts`'s `touchCloseHint`, invariant 5). Defaulted, like every
   * other panel's, so `new Hud(root)` in a check is unchanged.
   */
  constructor(root: HTMLElement, touch = false) {
    this.element = document.createElement("div");
    // **Not a `.hud__panel`**, which is the claim the reading window made one
    // chunk ago and for the same reason (`ui/reading.css`): the chassis in
    // `ui/panel.css` is a box in a corner with a plate and a padding, and a
    // full-frame layer would have had to cancel six of its declarations to
    // become one. `ui/menu.css` restates the `[hidden]` rule the chassis used
    // to lend it, because an author `display` on a flex layer beats the user
    // agent's `[hidden]`.
    this.element.className = "hud__panel--menu hud__menu";
    this.element.hidden = true;

    const panel = document.createElement("section");
    // The kit's panel: the hairline, the gold corner brackets and the plate.
    // Not the blur it also carries — that is cancelled in `ui/menu.css`, with
    // the three measurements that decided it (see the header).
    panel.className = "hud__menu-panel game-panel";

    this.strip = document.createElement("div");
    // **`.top-tabs` and never `.top-tabs--caps`** (invariant 4): the kit's opt-in
    // uppercase exists and this strip does not take it, because a tab strip is a
    // place a name eventually sits — a town's page, a person's page — and
    // `text-transform` on anything that can carry a name is this client's oldest
    // recorded UI bug, twice over (`ui/panel.css`'s note on the panel title).
    this.strip.className = "hud__menu-tabs top-tabs";
    this.strip.setAttribute("role", "tablist");

    const body = document.createElement("div");
    body.className = "hud__menu-body";

    for (const { page, label } of PAGES) {
      const tab = document.createElement("button");
      tab.type = "button";
      tab.className = "hud__menu-tab";
      tab.textContent = label;
      tab.setAttribute("role", "tab");
      // Read by the smoke scripts and by `touch-smoke.mjs` to press a tab the
      // way a thumb does — the same `data-verb` arrangement the touch pad's own
      // buttons carry (`input/touch.ts`).
      tab.dataset.page = page;
      tab.addEventListener("click", (event) => {
        // The click must not reach the canvas underneath, which is the pointer-
        // lock target: a player who pressed a tab did not ask to be put back
        // into the game (`hud/panels/shared.ts`'s `button`).
        event.stopPropagation();
        this.show(page);
      });

      const shell = document.createElement("div");
      shell.className = `${PAGE_NAMES[page]} hud__menu-page`;
      shell.hidden = true;
      shell.setAttribute("role", "tabpanel");

      this.strip.append(tab);
      body.append(shell);
      this.pages.set(page, { tab, body: shell, note: "" });
    }

    const foot = document.createElement("div");
    foot.className = "hud__menu-foot panel-footer";

    this.closeLine = document.createElement("div");
    this.closeLine.className = "hud__menu-close";
    // **Said once, for four pages**, which is the whole of what one screen buys
    // over three overlays. Composed on a touchscreen rather than typed
    // (invariant 5, `docs/decisions/input.md` §9): the hint names a button, the
    // label is a string `input/touch.ts` owns, and a renamed button would
    // otherwise leave this telling a player to press something that is not
    // there.
    //
    // **No Escape**, and that is a refusal rather than an omission: no panel in
    // this client has ever closed on Escape (`input.md` §9's table is six keys
    // and none of them is one), and in a browser Escape is what leaves the
    // pointer lock — binding it here would give one key two jobs on the one
    // surface that has just handed the mouse back.
    this.closeLine.textContent = touch ? touchCloseHint("menu") : "";

    if (!touch) {
      this.closeLine.append(chip("Tab"), " closes");
    }

    const jump = document.createElement("div");
    jump.className = "hud__menu-jump";

    if (!touch) {
      // The three keys that still open their own page, drawn as the chips every
      // prompt in this client draws a key as since chunk U-1. On a touchscreen
      // there are no keys and the strip is what a thumb presses, so the clause
      // is not written at all rather than written about something that is not
      // there.
      jump.append(chip("J"), chip("M"), chip("I"), " land on a page");
    }

    this.noteLine = document.createElement("div");
    // `push` is the kit's own way of saying *and this goes to the far end*.
    this.noteLine.className = "hud__menu-note push";

    foot.append(this.closeLine, jump, this.noteLine);
    panel.append(this.strip, body, foot);
    this.element.append(panel);
    root.append(this.element);
  }

  /** Whether the screen is up. The pointer-lock handshake's question. */
  get open(): boolean {
    return this.showing !== null;
  }

  /** Which page is up, or `null` when the screen is shut. For the self-checks. */
  get page(): MenuPage | null {
    return this.showing;
  }

  /** Where a bare `Tab` comes back to. */
  get lastPage(): MenuPage {
    return this.last;
  }

  /**
   * Hands one page over to the surface that fills it.
   *
   * Called once per page, at construction, by `hud/hud.ts`. What comes back is
   * a view of the screen four verbs wide, so a surface never learns what a
   * `MenuScreen` is ({@link MenuTab}).
   */
  tab(page: MenuPage): MenuTab {
    const held = this.pages.get(page);

    if (held === undefined) {
      // Unreachable: the map is built from `PAGES` and `MenuPage` is a closed
      // union, so a caller cannot name a page that is not in it. Thrown rather
      // than papered over, unlike the two guards in `input/touch.ts` that answer
      // with an empty string: those are asked at *draw* time about a label, and
      // this is asked at construction about where a whole surface attaches —
      // a page handed a body nobody owns would be a surface that silently never
      // appeared.
      throw new Error(`no menu page called ${page}`);
    }

    // The screen, captured, because a getter written into an object literal
    // would answer about the literal rather than about the screen.
    const screen = this;

    return {
      page,
      body: held.body,
      get showing(): boolean {
        return screen.showing === page;
      },
      reveal: () => {
        this.select(page);
      },
      close: () => {
        if (this.showing === page) {
          this.hide();
        }
      },
      note: (text: string) => {
        held.note = text;
        this.drawNote();
      },
    };
  }

  /**
   * Says who fills a page the strip switches to.
   *
   * The keys do not need this — `J` goes through the witnessing layer's own
   * toggle, which fills the page and then reveals it — but a tab pressed with a
   * finger or a mouse goes through nothing at all, and a page that is switched
   * to without being re-read is a page showing what the world looked like the
   * last time somebody opened it.
   */
  fills(filler: (page: MenuPage) => void): void {
    this.filler = filler;
  }

  /**
   * Opens the screen on a page, filling it first.
   *
   * The one door the strip, the touch button and the `Tab` key all come through.
   * The fill runs before the selection and is allowed to select a page of its
   * own on the way past — filling the journal reveals the journal — so the
   * selection here is what wins, which is how *you* is reachable at all: its
   * text is the journal's view read a second way.
   */
  show(page: MenuPage): void {
    this.filler?.(page);
    this.select(page);
  }

  /**
   * What `Tab` does: away if it is up, back to where it was left if it is not.
   */
  toggle(): void {
    if (this.showing === null) {
      this.show(this.last);

      return;
    }

    this.hide();
  }

  hide(): void {
    if (this.showing === null) {
      return;
    }

    for (const held of this.pages.values()) {
      held.body.hidden = true;
      held.tab.classList.remove("active");
      held.tab.setAttribute("aria-selected", "false");
    }

    this.showing = null;
    this.element.hidden = true;
  }

  /**
   * Puts one page on screen and takes the other three off.
   *
   * **Written only when it moved** (invariant 8): the map asks to be revealed
   * every time the ground under the player's feet grows, which is every couple
   * of seconds while it is open (`witness/witness.ts`), and four class writes
   * and four `hidden` writes for a page that was already showing is four
   * writes for nothing.
   */
  private select(page: MenuPage): void {
    this.last = page;

    if (this.showing === page) {
      return;
    }

    this.showing = page;

    for (const [named, held] of this.pages) {
      const on = named === page;

      held.body.hidden = !on;
      held.tab.classList.toggle("active", on);
      held.tab.setAttribute("aria-selected", String(on));
    }

    this.element.hidden = false;
    this.drawNote();
  }

  /** The showing page's own sentence, at the far end of the foot. */
  private drawNote(): void {
    const held = this.showing === null ? undefined : this.pages.get(this.showing);

    this.noteLine.textContent = held?.note ?? "";
  }
}

/**
 * A key, drawn as the kit's `kbd`.
 *
 * Chunk U-1's rule — *a prompt reads as press this → this happens before it is
 * read at all* — applied to the one foot in this client that names four keys.
 * It carries the client's own `--hud-bg` for U-1's own reason: the kit's plate
 * is a lightening, and a lightening is what a key looks like on a dark panel.
 */
function chip(key: string): HTMLElement {
  const element = document.createElement("kbd");
  element.className = "hud__menu-key";
  element.textContent = key;

  return element;
}
